"use client";

import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Stack,
} from "@mui/material";
import { ArrowBack, Favorite, FavoriteBorder } from "@mui/icons-material";
import { useAuth } from "../contexts/AuthContext";
import { useWishlist } from "../contexts/WishlistContext";
import { useToast } from "../contexts/ToastContext";
import apiEndpoints from "../apiConfig";
import ProfileGrid from "../components/profile/ProfileGrid";

function WishlistPage() {
  const { user, logout } = useAuth();

  const { wishlist } = useWishlist();
  const navigate = useNavigate();

  const { showToast } = useToast(); // Import useToast

  useEffect(() => {
    if (wishlist.length === 0) {
      showToast("Your wishlist is empty", "info");
    }
  }, [wishlist.length]);

  const handleViewProfile = (profileId) => {
    navigate(`/profile/${profileId}`);
  };

  const handleAddToWishlist = (profileId) => {
    // handled by context
  };

  const handleSendEnquiry = (receiverId) => {
    fetch(apiEndpoints.sendEnquiry, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        requester_id: user.user_id,
        receiver_id: receiverId,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "success") {
          showToast("Enquiry sent successfully", "success");
        } else if (data.status === "exists") {
          showToast("Enquiry already requested", "info");
        } else {
          showToast(data.message || "Failed to send enquiry", "error");
        }
      })
      .catch((err) => {
        console.error("Error sending enquiry:", err);
        showToast("something went wrong", "error");
      });
  };

  // Merge profiles with onSendEnquiry
  const profilesWithProps = wishlist.map((p) => ({
    ...p,
    onSendEnquiry: handleSendEnquiry,
  }));

  if (!user) {
    return null;
  }

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "background.default" }}>
      {/* Header */}
      <Container maxWidth="100%" gap={2}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: { xs: 1, sm: 1 },
            px: { xs: 2, sm: 2 },
          }}
        >
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate("/home")}
            color="inherit"
            sx={{
              color: "black",
              fontSize: { xs: "0.65rem", sm: "0.85rem", md: "1rem" },
              minWidth: "auto",
              mx: -5,
            }}
          >
            Back
          </Button>
        </Box>
      </Container>
      {/* </Paper> */}

      <Container maxWidth="100%" sx={{ py: 1 }}>
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h3"
            sx={{ fontWeight: 700, mb: 1, color: "black" }}
          >
            Your Wishlist
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {wishlist.length > 0
              ? `You have saved ${wishlist.length} profile${
                  wishlist.length === 1 ? "" : "s"
                }`
              : "Your wishlist is empty"}
          </Typography>
        </Box>

        {wishlist.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <FavoriteBorder sx={{ fontSize: 64, color: "grey", mb: 2 }} />
            <Typography
              variant="h5"
              sx={{ mb: 2, color: "black", fontWeight: 600 }}
            >
              Your wishlist is empty
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mb: 3, maxWidth: 500, margin: "0 auto" }}
            >
              Start adding profiles to your wishlist to keep track of people
              you're interested in connecting with.
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="center" mt={2}>
              <Button
                variant="contained"
                onClick={() => navigate("/home")}
                color="secondary"
              >
                Browse Profiles
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate("/search")}
                color="inherit" // or secondary
                sx={{ borderColor: "text.primary", color: "text.primary" }}
              >
                Advanced Search
              </Button>
            </Stack>
          </Box>
        ) : (
          <ProfileGrid
            profiles={profilesWithProps}
            onViewProfile={handleViewProfile}
            onAddToWishlist={handleAddToWishlist}
          />
        )}
      </Container>
    </Box>
  );
}

export default WishlistPage;
